/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import * as React from 'react';

import { Chip, breakpoints } from '@coursera/cds-core';

type WithSvgIconVariant = {
  variant: 'withSvgIcon';
  iconBackgroundColor?: string;
  icon: React.ReactElement;
};

type WithImageIconVariant = {
  variant: 'withImageIcon';
  icon: React.ReactElement;
};

type TextOnlyVariant = {
  variant: 'textOnly';
  spacing?: 'small' | 'medium';
};

export type OptionPillProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
} & (WithSvgIconVariant | WithImageIconVariant | TextOnlyVariant);

const styles = {
  withSvgIcon: ({ iconBackgroundColor }: { iconBackgroundColor: string }) => css`
    &.cds-Chip-root {
      padding-right: var(--cds-spacing-200);
    }

    .cds-Chip-label .cds-Chip-startIcon {
      width: 28px;
      height: 28px;
      background-color: ${iconBackgroundColor};
      margin-right: var(--cds-spacing-100);

      svg {
        width: var(--cds-spacing-200);
        height: var(--cds-spacing-200);
      }
    }

    ${breakpoints.down('xs')} {
      &.cds-Chip-root {
        padding-right: var(--cds-spacing-100);

        .cds-Chip-label .cds-Chip-startIcon {
          width: var(--cds-spacing-200);
          height: var(--cds-spacing-200);
          margin-right: var(--cds-spacing-50);

          svg {
            width: 10px;
            height: 10px;
          }
        }
      }
    }
  `,
  withImageIcon: css`
    &.cds-Chip-root {
      .cds-Chip-label .cds-Chip-startIcon {
        width: var(--cds-spacing-200);
        height: var(--cds-spacing-200);
        margin-right: var(--cds-spacing-50);

        img {
          border-radius: 50%;
          width: var(--cds-spacing-200);
          height: var(--cds-spacing-200);
        }
      }
    }
  `,
  textOnly: ({ spacing }: { spacing: 'small' | 'medium' }) => css`
    &.cds-Chip-root {
      padding: var(--cds-spacing-100) ${spacing === 'small' ? 'var(--cds-spacing-100)' : 'var(--cds-spacing-150)'};
    }
  `,
  customChip: css`
    &.cds-Chip-root {
      padding: var(--cds-spacing-100);
      border-color: var(--cds-color-grey-50);
      font-weight: 400;

      .cds-Chip-label .cds-Chip-startIcon {
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-grey-900);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-interactive-primary-hover);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-interactive-primary);
      background-color: var(--cds-color-white-0);
      border-color: var(--cds-color-interactive-primary);
    }

    &.cds-Chip-root:disabled {
      color: var(--cds-color-grey-500);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-grey-100);
    }
  `,
};

/**
 * CDS Chip component with custom styles. This should be used in a CDS ChipGroup.
 */
const OptionPill = (props: OptionPillProps) => {
  const { label, selected, onClick, variant, disabled } = props;
  let variantStyles: SerializedStyles | undefined;

  switch (variant) {
    case 'withSvgIcon': {
      const { iconBackgroundColor = 'var(--cds-color-emphasis-primary-background-strong)' } = props;
      variantStyles = styles.withSvgIcon({ iconBackgroundColor });
      break;
    }

    case 'withImageIcon':
      variantStyles = styles.withImageIcon;
      break;

    case 'textOnly': {
      const { spacing = 'medium' } = props;
      variantStyles = styles.textOnly({ spacing });
      break;
    }

    default:
      variantStyles = undefined;
  }

  return (
    <Chip
      icon={variant !== 'textOnly' ? props.icon : undefined}
      onClick={onClick}
      variant="select"
      selected={selected}
      disabled={disabled}
      css={[styles.customChip, variantStyles]}
    >
      {label}
    </Chip>
  );
};

export default OptionPill;
